import React, { useState, useEffect } from 'react';
import { devCenter } from '../../services/api';
import { Terminal, Database } from 'lucide-react';

const SessionLogViewer = () => {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        fetchSessions();
    }, []);

    const fetchSessions = async () => {
        try {
            const res = await devCenter.getSessions();
            setSessions(res.data);
            if (res.data.length > 0) setSelected(res.data[0]);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="text-slate-500">Loading session logs...</div>;

    return (
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-1 border-r border-slate-700 pr-4 space-y-2 max-h-[600px] overflow-y-auto">
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4 border-b border-slate-700 pb-2">Session History</h3>
                {sessions.length === 0 ? (
                    <div className="text-xs text-slate-500 text-center py-4">No sessions.</div>
                ) : (
                    sessions.map(s => (
                        <div 
                            key={s.id} 
                            onClick={() => setSelected(s)}
                            className={`p-3 rounded-lg cursor-pointer transition-colors ${selected?.id === s.id ? 'bg-blue-600 border-blue-500' : 'bg-slate-900 border-slate-800 hover:bg-slate-800'} border mb-2`}
                        >
                            <div className="text-xs text-slate-300 font-medium truncate">{s.objective}</div>
                            <div className="text-[10px] text-slate-500 mt-1">{new Date(s.created_at).toLocaleString()}</div>
                        </div>
                    ))
                )}
            </div>
            
            <div className="md:col-span-2">
                {selected ? (
                    <div className="space-y-6">
                        <div className="flex justify-between items-start border-b border-slate-700 pb-4">
                            <div>
                                <h2 className="text-xl font-medium text-white">{selected.objective}</h2>
                                <p className="text-xs text-slate-400 mt-1 flex items-center gap-2">
                                    <span className="bg-slate-700 px-1.5 py-0.5 rounded uppercase text-[10px] text-slate-300 font-bold">{selected.source}</span>
                                    {new Date(selected.created_at).toLocaleString()}
                                </p>
                            </div>
                        </div>

                        <div className="space-y-4 text-sm">
                            <div>
                                <h4 className="flex items-center gap-2 text-slate-400 font-medium mb-2"><Terminal size={14}/> Change Summary</h4>
                                <div className="bg-slate-900 p-4 rounded-lg font-mono text-xs text-slate-300 whitespace-pre-wrap">
                                    {selected.exact_change_summary || "No summary recorded."}
                                </div>
                            </div>

                            {selected.files_touched && (
                                <div>
                                    <h4 className="flex items-center gap-2 text-slate-400 font-medium mb-2"><Database size={14}/> Files Touched</h4>
                                    <div className="bg-slate-900 p-4 rounded-lg font-mono text-xs text-emerald-400 whitespace-pre-wrap">
                                        {selected.files_touched}
                                    </div>
                                </div>
                            )}

                            {selected.warnings && (
                                <div className="bg-amber-950/30 border border-amber-900/50 p-4 rounded-lg">
                                    <h4 className="text-amber-500 font-medium text-xs mb-1 uppercase tracking-wider">Warnings</h4>
                                    <p className="text-amber-200/80 text-xs">
                                        {selected.warnings}
                                    </p>
                                </div>
                            )}
                            
                            {selected.testing_notes && (
                                <div>
                                    <h4 className="text-slate-400 font-medium mb-2 text-xs uppercase tracking-wider">Testing Notes</h4>
                                    <p className="text-slate-300 text-sm bg-slate-800 p-4 rounded-lg">
                                        {selected.testing_notes}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="flex h-full items-center justify-center text-slate-500 text-sm">Select a session to view details.</div>
                )}
            </div>
        </div>
    );
};

export default SessionLogViewer;
